// Shared helpers for the Tillgångar (fixed assets) UI.
//
// Formatting follows the app-wide convention (sv-SE + SEK via Intl), the
// same shape as the local formatCurrency helpers in SupplierInvoiceList.js
// and ManualBookkeepingEntry.jsx — centralised here since all asset views
// need them. Amounts arrive from the backend as DECIMAL strings; every
// helper parses defensively and never does math on formatted strings.

import { Laptop, Car, Wrench, Armchair, Building2, Box, Home, Mountain, TreePine } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { aiOperationHeaders } from '../../../../services/AiOperation';

/**
 * AI classification prefill (A3): POST /assets/detection/classify with free
 * text from the source document. Gated server-side (ai_asset_detection
 * feature + asset_classification credits) — 402/403 mean "not on this plan /
 * out of credits" and MUST degrade silently to the deterministic prefill
 * (credit-aware degradation rule: less prefill, never a blocked flow).
 * Returns {category_code, name, useful_months, quantity, confidence} or null.
 */
export const classifyAssetText = async (companyId, text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  try {
    const res = await axios.post(
      getApiUrl(`${companyId}/assets/detection/classify`),
      { text: trimmed.slice(0, 4000) },
      { withCredentials: true, headers: aiOperationHeaders() }
    );
    const data = res.data?.data ?? res.data;
    return data?.classification || null;
  } catch (e) {
    return null;
  }
};

export const round2 = (value) => Math.round((parseFloat(value) || 0) * 100) / 100;

export const formatCurrency = (amount, decimals = 0) =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parseFloat(amount) || 0);

export const formatNumber = (amount, decimals = 2) =>
  new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parseFloat(amount) || 0);

// 'YYYY-MM' → Date (first of month). Returns null on bad input.
export const periodToDate = (period) => {
  if (!period) return null;
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
};

export const dateToPeriod = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const addMonths = (date, n) =>
  new Date(date.getFullYear(), date.getMonth() + n, 1);

export const lastDayOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const monthDiff = (from, to) =>
  (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());

// Accepts a Date, a 'YYYY-MM' period or a date string → "aug. 2029" / "augusti 2029"
export const formatMonthYear = (value, style = 'short') => {
  let date = value instanceof Date ? value : periodToDate(value);
  if (!date && value) date = new Date(value);
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('sv-SE', {
    month: style === 'long' ? 'long' : 'short',
    year: 'numeric',
  });
};

export const formatDate = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('sv-SE');
};

// Consistent response unwrapping — the API sometimes wraps in { data: ... }.
export const unwrapData = (response) => response?.data?.data ?? response?.data;

// Engine model identifiers (server-owned; the browser never selects one).
export const K1_COLLECTIVE_MODEL = 'k1_collective_v1';
export const K2_INDIVIDUAL_MODEL = 'k2_individual_v1';

/** True when the tenant's active asset model is K1:s kollektiva modell. */
export const isK1Collective = (capability) =>
  capability?.assetAccountingModel === K1_COLLECTIVE_MODEL;

export const K1_ASSET_ACCOUNTING_MODEL_UNSUPPORTED =
  'K1_ASSET_ACCOUNTING_MODEL_UNSUPPORTED';
export const K3_ASSET_ACCOUNTING_MODEL_UNSUPPORTED =
  'K3_ASSET_ACCOUNTING_MODEL_UNSUPPORTED';
export const ASSET_ACCOUNTING_REGIME_UNSUPPORTED =
  'ASSET_ACCOUNTING_REGIME_UNSUPPORTED';
export const ASSET_ACCOUNTING_MODEL_WITHDRAWN =
  'ASSET_ACCOUNTING_MODEL_WITHDRAWN';
export const ASSET_ENGINE_MODEL_MISMATCH = 'ASSET_ENGINE_MODEL_MISMATCH';

/**
 * K1-specific structured refusals (docs/bas-2026/K1_ASSET_ACCOUNTING_SPEC.md
 * §11) → Swedish explanations. Unknown codes get the generic fallback so raw
 * internal reasons never reach the UI.
 */
export const K1_REASON_MESSAGES = {
  ASSET_ENGINE_MODEL_MISMATCH:
    'Företaget använder K1:s kollektiva tillgångsmodell — den här funktionen tillhör den individuella K2-modellen. Använd årsvärderingen under Tillgångar i stället.',
  K1_FISCAL_YEAR_BEFORE_2025_UNSUPPORTED:
    'K1-motorn stöder räkenskapsår som börjar 2025-01-01 eller senare.',
  K1_FISCAL_YEAR_NON_WHOLE_MONTHS:
    'Räkenskapsåret börjar eller slutar mitt i en månad — jämkningen kan inte fastställas maskinellt. Kontakta support.',
  K1_POOL_OPENING_UNRECONCILED:
    'Föregående års omföring av Årets-kontona saknas — årets värdering kan inte beräknas förrän ingående saldon är avstämda.',
  K1_VEHICLE_VAT_PROFILE_REQUIRED:
    'Ange momsbehandling för fordonet: personbil (ingen momslyft, momsfri försäljning) eller lastbil/standard (avdragsgill moms, utgående moms vid försäljning).',
  ASSET_ACQUISITION_ROW_MISMATCH:
    'Verifikatets rader debiterar inte kategorins bokföringskonto med anskaffningsvärdet — kontrollera konteringen och försök igen.',
  ASSET_PERSONBIL_INPUT_VAT_ROW:
    'Personbil: ingen ingående moms får lyftas vid köpet — momsen ska ingå i anskaffningsvärdet. Gäller fordonet avdragsgill moms, välj lastbil/arbetsfordon.',
  K1_PROPERTY_PARENT_INVALID:
    'Förbättringens huvudbyggnad måste vara en aktiv K1-byggnad i registret.',
  K1_PROPERTY_LINKED_IMPROVEMENTS_ACTIVE:
    'Byggnaden har aktiva länkade förbättringar som måste avvecklas först (eller samtidigt).',
  K1_PROPERTY_MANUAL_PLAN_DISPOSAL_MANUAL:
    'Tillgången har en manuell avskrivningsplan — även avyttringen bokförs manuellt (registret känner inte de manuella avskrivningarna).',
  K1_PROPERTY_MANUAL_PLAN_DISABLE_BLOCKED:
    'Manuell avskrivningsplan kan inte stängas av — årsvärderingar har bokförts medan tillgången funnits. Kontakta support för en kontrollerad avstämning.',
  K1_RECONCILIATION_EVIDENCE_STALE:
    'Underlaget har ändrats efter bekräftelsen — generera, granska och bekräfta avstämningen på nytt.',
  K1_RECONCILIATION_NOT_CONFIRMED:
    'Ingen bekräftad ingående poolavstämning finns för räkenskapsåret.',
  K1_PROPERTY_MANUAL_PLAN_INVALID_CATEGORY:
    'Manuell avskrivningsplan gäller endast byggnader och markanläggningar (K1).',
  K1_POOL_LEDGER_REGISTER_MISMATCH:
    'Bokföringen och tillgångsregistret stämmer inte överens. Stäm av registret mot bokföringen — årets värdering kan inte beräknas eller bokföras förrän differensen är utredd.',
  K1_PROPERTY_CLASSIFICATION_REQUIRED:
    'En byggnad eller markanläggning saknar skattemässig klassificering (SKV A 2005:5 / IL 20 kap. 5 §). Komplettera tillgången innan värderingen kan bokföras.',
  K1_POOL_ALREADY_POSTED: 'Årets värdering är redan bokförd för räkenskapsåret.',
  K1_POOL_LATER_YEAR_POSTED: 'Ett senare räkenskapsår är redan värderat — ångra det först.',
  K1_PREVIEW_STALE: 'Underlaget har ändrats — hämta en ny förhandsgranskning och bekräfta igen.',
  K1_SELECTED_VALUE_OUT_OF_BAND: 'Det valda värdet ligger utanför det tillåtna intervallet.',
  K1_SUPPL_RULE_UNAVAILABLE: 'Kompletteringsregeln är inte tillgänglig för det här räkenskapsåret.',
  K1_LOW_VALUE_UNAVAILABLE: 'Full avskrivning kräver att underlaget är högst ett halvt prisbasbelopp.',
  K1_TAX_FACTS_UNAVAILABLE: 'Skattefakta för året kunde inte hämtas — försök igen senare.',
  K1_PARTIAL_DISPOSAL_UNSUPPORTED: 'Grupperade poster avvecklas i sin helhet i K1.',
  K1_ASSET_NOT_ACTIVE: 'Tillgången är redan avvecklad eller inte aktiv.',
  K1_CATEGORY_TUPLE_DRIFTED:
    'Tillgångskategorins konton avviker från den normativa K1-modellen — kontakta support innan värderingen kan bokföras.',
  K1_PROPERTY_MANUAL_PLAN:
    'Byggnaden har en fastställd skattemässig plan som avviker från Skatteverkets normala procentsatser — årets avskrivning bokförs manuellt.',
  K1_RECONCILIATION_WRONG_CHART:
    'Ingående poolavstämning gäller endast historiska BAS2024-tenants.',
  K1_RECONCILIATION_REPORT_STALE:
    'Underlaget har ändrats sedan rapporten genererades — generera om och granska igen.',
  FISCAL_YEAR_CLOSED: 'Räkenskapsåret är stängt.',
};

export const k1ReasonMessage = (errorOrPayload) => {
  const payload = errorOrPayload?.response?.data ?? errorOrPayload;
  const details = payload?.details || payload;
  const reason = details?.reason;
  if (reason && K1_REASON_MESSAGES[reason]) return K1_REASON_MESSAGES[reason];
  return payload?.error || 'Åtgärden kunde inte genomföras.';
};

// K1 valuation alternatives (IL 18 kap. 13/17 §§; BFNAR 2006:1 p. 6.38).
export const K1_ALTERNATIVE_LABELS = {
  main_rule: 'Huvudregeln (30 %)',
  suppl_rule: 'Kompletteringsregeln (20 % per år)',
  per_kind: 'Per slag (IL 18 kap. 17 §) — olika regel för olika slag av inventarier',
  low_value_full: 'Full avskrivning (underlag högst ett halvt prisbasbelopp)',
  custom: 'Eget värde',
};

// IL 18:17 2 men. — statutory kinds selectable separately.
export const K1_STATUTORY_KIND_LABELS = {
  tangible: 'Maskiner och inventarier (inkl. byggnads-/markinventarier och fordon)',
  intangibles: 'Förvärvade immateriella tillgångar',
};

// Backend classification keys → labels (regulatory_constants
// avskrivning_byggnad_<typ>_rate; SKV A 2005:5).
export const K1_BUILDING_TYPES = [
  { value: 'smahus', label: 'Småhus', percent: 2 },
  { value: 'hyreshus_parkering_varuhus_hotell', label: 'Parkeringshus, varuhus, hotell & restaurang', percent: 3 },
  { value: 'hyreshus_kiosk', label: 'Kiosk', percent: 5 },
  { value: 'hyreshus_ovriga', label: 'Övriga hyreshus', percent: 2 },
  { value: 'ekonomibyggnad_vaxthus_silo_kylhus', label: 'Växthus, silor, kylhus', percent: 5 },
  { value: 'ekonomibyggnad_ovriga', label: 'Övriga ekonomibyggnader', percent: 4 },
  { value: 'kraftverk_varme', label: 'Värmekraftsbyggnad', percent: 4 },
  { value: 'kraftverk_vatten', label: 'Vattenkraftsbyggnad', percent: 2 },
  { value: 'industri_generell', label: 'Industribyggnad, generell användning', percent: 4 },
  { value: 'industri_begransad', label: 'Industribyggnad, begränsad användbarhet', percent: 5 },
  { value: 'specialbyggnad', label: 'Specialbyggnad', percent: 3 },
];

export const K1_LAND_IMPROVEMENT_TYPES = [
  { value: 'tackdiken_skogsvagar', label: 'Täckdiken och skogsvägar', percent: 10 },
  { value: 'other', label: 'Andra markanläggningar', percent: 5 },
];

/** Map the backend's stable asset-policy result without guessing from status
 * or Swedish prose. Accepts an Axios error or an already-unwrapped payload. */
export const getAssetAccountingUnavailable = (errorOrPayload) => {
  const payload = errorOrPayload?.response?.data ?? errorOrPayload;
  const details = payload?.details || payload?.accountingCapability || payload;
  if (![
    K1_ASSET_ACCOUNTING_MODEL_UNSUPPORTED,
    K3_ASSET_ACCOUNTING_MODEL_UNSUPPORTED,
    ASSET_ACCOUNTING_REGIME_UNSUPPORTED,
    ASSET_ACCOUNTING_MODEL_WITHDRAWN,
  ]
    .includes(details?.reason)) return null;
  const isK3 = details.reason === K3_ASSET_ACCOUNTING_MODEL_UNSUPPORTED;
  const isUnclassified = details.reason === ASSET_ACCOUNTING_REGIME_UNSUPPORTED;
  const isWithdrawn = details.reason === ASSET_ACCOUNTING_MODEL_WITHDRAWN;
  const implicitK1 = !isK3 && !isUnclassified && !isWithdrawn
    && details.accountingRegimeExplicit === false;
  return {
    reason: details.reason,
    chartVersion: details.chartVersion || null,
    accountingRegime: details.accountingRegime ?? (isK3 ? 'k3' : isUnclassified ? null : 'k1'),
    accountingRegimeExplicit: details.accountingRegimeExplicit !== false,
    operation: details.operation || null,
    classificationRequired: details.classificationRequired === true,
    classificationAllowed: details.classificationAllowed === true,
    companyType: details.companyType || null,
    heading: isWithdrawn
      ? 'K2-kompatibiliteten har återkallats'
      : isK3
      ? 'K3-stöd för tillgångsbokföring saknas'
      : isUnclassified
        ? 'Redovisningsregelverket stöds inte av tillgångsmodulen'
        : 'K1-stöd för tillgångsbokföring saknas',
    message: isWithdrawn
      ? 'Den tidigare bekräftelsen gäller inte längre. Sparade registeruppgifter kan läsas, men K2-beräkningar och bokföring är avstängda.'
      : isK3
      ? 'Tillgångsmodulens K2-motor stöder inte K3:s komponentredovisning ännu. Det förhindrar att K2-regler används av misstag.'
      : isUnclassified
        ? 'Företagets regelverk är inte klassificerat som K2. Sparade registeruppgifter kan läsas, men K2-beräkningar och bokföring är avstängda.'
        : implicitK1
        ? 'Inget regelverk har valts för den enskilda firman, så den behandlas tills vidare som K1. Om företaget faktiskt tillämpar K2 kan ägaren ange det under Inställningar.'
        : 'Tillgångsmodulens avskrivningar och bokföring stöds inte för K1 ännu. Det förhindrar att K2-regler används av misstag.',
  };
};

// ---------------------------------------------------------------------------
// CSV helpers (Swedish-Excel-friendly: BOM + semicolon delimiter + comma
// decimals). Shared by AssetRegisterReport, AssetNoteReport and the importer.
// ---------------------------------------------------------------------------

export const toCsvValue = (value) => {
  const text = String(value ?? '');
  return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const csvNumber = (value) => round2(value).toFixed(2).replace('.', ',');

// lines: array of already-joined CSV line strings.
export const downloadCsv = (lines, filename) => {
  const csv = '\uFEFF' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Category presets (mirror of the seeded asset_categories codes; the
// authoritative rows — incl. account numbers per kontoplan vintage — live in
// the tenant DB and are fetched at runtime where available).
export const ASSET_CATEGORIES = [
  { code: 'computers', label: 'Datorer & elektronik' },
  // K1 (BAS 2018 K1) classes — spec docs/bas-2026/K1_ASSET_ACCOUNTING_SPEC.md §2.
  { code: 'k1_machines_inventory', label: 'Maskiner och inventarier' },
  { code: 'k1_building_inventory', label: 'Byggnads- och markinventarier' },
  { code: 'k1_vehicles', label: 'Bilar och andra transportmedel' },
  { code: 'k1_intangibles', label: 'Immateriella tillgångar' },
  { code: 'k1_buildings', label: 'Byggnader' },
  { code: 'k1_land', label: 'Mark' },
  { code: 'k1_land_improvements', label: 'Markanläggningar' },
  { code: 'vehicles', label: 'Fordon' },
  { code: 'machines', label: 'Maskiner & verktyg' },
  { code: 'furniture', label: 'Möbler & inredning' },
  { code: 'improvements_leasehold', label: 'Förbättring på hyrd lokal' },
  { code: 'buildings', label: 'Byggnader' },
  { code: 'land', label: 'Mark' },
  { code: 'land_improvements', label: 'Markanläggningar' },
  { code: 'other', label: 'Övrigt' },
];

// There is deliberately no bundled fallback account map any more: asset
// accounts are tenant configuration (asset_categories rows, vintage-correct
// per chart). A category without a booking account — or a failed category
// fetch — yields no inline option; the full asset form remains the path.
// (The old map guessed freed BAS2024 numbers like 1240/1250, which are wrong
// on a BAS2026 chart.)

/**
 * Category options for the INLINE book-as-asset flows (expense and
 * supplier-invoice booking). Shared by BookExpenseModal and
 * VerificationModal so the caller contract stays uniform (review
 * remediation F3):
 * - the booking debit account is the category's BOOKING account — on K1
 *   pool classes the year's acquisition account (12x1, BFNAR 2006:1 /
 *   BAS 2018 K1), never the base account;
 * - K1 property/land classes are excluded — they need the tax
 *   classification and belong in the full asset form;
 * - a category row without a booking account, or a missing tenant category
 *   list, yields NO inline option — the account is tenant configuration and
 *   is never guessed from a bundled vintage-specific map (Phase 6).
 */
export function inlineAssetCategoryOptions(tenantCategories) {
  if (!tenantCategories || tenantCategories.length === 0) return [];
  return tenantCategories
    .filter((c) => !['k1_property_tax_linked', 'k1_non_depreciable'].includes(c.accounting_model))
    .map((c) => ({
      code: c.code,
      label: c.name_sv || c.code,
      asset_account: String(c.acquisition_account || c.asset_account || ''),
    }))
    .filter((option) => option.asset_account !== '');
}


// SKV A 2005:5 — procentsatser för värdeminskningsavdrag per byggnadstyp.
// BFNAR 2017:3 p. 9.31 / K2 10.31 let the BOOK plan follow these, so picking
// a byggnadstyp seeds nyttjandeperiod = 100/procent years. Source saved in
// docs/sources/skv-a-2005-5.md.
export const BUILDING_TYPES = [
  { value: 'smahus', label: 'Småhus', percent: 2 },
  { value: 'parkeringshus_varuhus_hotell', label: 'Parkeringshus, varuhus, hotell & restaurang', percent: 3 },
  { value: 'kiosk', label: 'Kiosk', percent: 5 },
  { value: 'ovriga_hyreshus', label: 'Övriga hyreshus', percent: 2 },
  { value: 'vaxthus_silo_kylhus', label: 'Växthus, silor, kylhus', percent: 5 },
  { value: 'ovriga_ekonomibyggnader', label: 'Övriga ekonomibyggnader', percent: 4 },
  { value: 'industri_generell', label: 'Industribyggnad (generell användning)', percent: 4 },
  { value: 'industri_speciell', label: 'Industribyggnad (begränsad användbarhet — t.ex. bensinstation, kylhus, sågverk)', percent: 5 },
  { value: 'specialbyggnad', label: 'Specialbyggnad', percent: 3 },
];

export const getCategoryMeta = (code) =>
  ASSET_CATEGORIES.find((c) => c.code === code) || ASSET_CATEGORIES[ASSET_CATEGORIES.length - 1];

// Category code → lucide line icon. THE single mapping — the emoji strings in
// asset_categories.icon (tenant DB) are legacy and deliberately ignored by
// the UI; everything renders these line icons from the app's icon set instead.
export const CATEGORY_ICONS = {
  computers: Laptop,
  k1_machines_inventory: Wrench,
  k1_building_inventory: Building2,
  k1_vehicles: Car,
  k1_intangibles: Box,
  k1_buildings: Home,
  k1_land: Mountain,
  k1_land_improvements: TreePine,
  vehicles: Car,
  machines: Wrench,
  furniture: Armchair,
  improvements_leasehold: Building2,
  buildings: Home,
  land: Mountain,
  land_improvements: TreePine,
  other: Box,
};

export const getCategoryIcon = (code) => CATEGORY_ICONS[code] || Box;

// Digits-style tinted rounded-square icon chip for a category. Shared by the
// register rows/cards, detail header, nudge card, create form and importer so
// the category iconography is identical everywhere.
export const CategoryChip = ({ code, size = 34, iconSize = 17, tone = 'brand', style }) => {
  const Icon = getCategoryIcon(code);
  const tones = {
    brand: { background: 'var(--rgba-primary-1)', color: 'var(--primary)' },
    neutral: { background: '#f3f4f6', color: '#6b7280' },
  };
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(8, Math.round(size * 0.28)),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...(tones[tone] || tones.brand),
        ...style,
      }}
    >
      <Icon size={iconSize} strokeWidth={1.75} />
    </span>
  );
};

export const ASSET_STATUS_META = {
  draft: { label: 'Utkast', color: '#92400e', bg: '#fef3c7' },
  active: { label: 'Aktiv', color: '#166534', bg: '#dcfce7' },
  fully_depreciated: { label: 'Helt avskriven', color: '#1e40af', bg: '#dbeafe' },
  disposed: { label: 'Avvecklad', color: '#475569', bg: '#f1f5f9' },
};

// One badge component instead of the three near-identical inline copies that
// used to live in AssetList/AssetDetail/RunHistory.
export const StatusPill = ({ status, size = 'sm' }) => {
  const meta = ASSET_STATUS_META[status] || ASSET_STATUS_META.active;
  return (
    <span
      style={{
        background: meta.bg,
        color: meta.color,
        borderRadius: 999,
        padding: size === 'sm' ? '2px 10px' : '3px 12px',
        fontSize: size === 'sm' ? '0.75rem' : '0.8125rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  );
};

// Modal form chrome shared by every asset modal (create/dispose/import/edit).
// The theme's default .form-control border is nearly invisible on white —
// this gives the bordered + focus-ring treatment the Settings pages use
// (.modern-input in CompanySettings), scoped under .asset-form.
export const ASSET_FORM_STYLES = `
  .asset-form .form-control,
  .asset-form .form-select {
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    font-size: 0.875rem;
    padding: 0.6rem 0.9rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .asset-form .form-control:hover { border-color: #cbd5e1; }
  .asset-form .form-control:focus,
  .asset-form .form-select:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--rgba-primary-1);
  }
  .asset-form .form-control::placeholder { color: #9ca3af; }
  .asset-form .form-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.375rem;
  }
  .asset-form .input-group-text {
    border: 2px solid #e2e8f0;
    border-left: none;
    background: #f9fafb;
    color: #6b7280;
    font-size: 0.8125rem;
    font-weight: 600;
    border-radius: 0 8px 8px 0;
  }
  .asset-form .input-group .form-control { border-radius: 8px 0 0 8px; }
  .asset-form .react-datepicker-wrapper { display: block; width: 100%; }
  .asset-form .form-check-input:checked {
    background-color: var(--primary);
    border-color: var(--primary);
  }
  .asset-form .form-check-input:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--rgba-primary-1);
  }
  /* App-standard dense table for schedule/verifikat previews. */
  .asset-form .asset-table thead th {
    background: #f9fafb;
    color: #6b7280;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e5e7eb;
  }
  .asset-form .asset-table tbody td { border-color: #f3f4f6; }
  .asset-form .asset-num { font-variant-numeric: tabular-nums; }
`;

export const RUN_STATUS_META = {
  posted: { label: 'Bokförd', color: '#166534', bg: '#dcfce7' },
  pending: { label: 'Pågår', color: '#92400e', bg: '#fef3c7' },
  failed: { label: 'Misslyckad', color: '#b91c1c', bg: '#fee2e2' },
  skipped: { label: 'Överhoppad', color: '#475569', bg: '#f1f5f9' },
};

/**
 * Event-type icon metadata (Digits-style tinted event chips): lucide icon
 * name + tint role. Consumed via getEventMeta() so the timeline and plan
 * ledger render one consistent visual language. Colors are semantic:
 * brand for value-in events, neutral for recurring mechanics, amber for
 * value-out, red only for scrapping (loss of the thing itself).
 */
export const EVENT_TYPE_META = {
  acquisition: { icon: 'ShoppingCart', tone: 'brand' },
  opening_balance: { icon: 'Archive', tone: 'neutral' },
  depreciation: { icon: 'TrendingDown', tone: 'neutral' },
  disposal_sale: { icon: 'Banknote', tone: 'amber' },
  disposal_scrap: { icon: 'Trash2', tone: 'red' },
  disposal_insurance: { icon: 'Banknote', tone: 'amber' },
  partial_disposal: { icon: 'Scissors', tone: 'amber' },
  writedown: { icon: 'ArrowDownCircle', tone: 'amber' },
  writedown_reversal: { icon: 'ArrowUpCircle', tone: 'brand' },
  revaluation: { icon: 'ArrowUpCircle', tone: 'brand' },
  adjustment: { icon: 'SlidersHorizontal', tone: 'neutral' },
  correction: { icon: 'Pencil', tone: 'neutral' },
  reclassification: { icon: 'Shuffle', tone: 'neutral' },
};

export const EVENT_TONE_STYLES = {
  brand: { background: 'var(--rgba-primary-1)', color: 'var(--primary)' },
  neutral: { background: '#f1f5f9', color: '#64748b' },
  amber: { background: '#fef3c7', color: '#b45309' },
  red: { background: '#fee2e2', color: '#b91c1c' },
};

export const EVENT_TYPE_LABELS = {
  acquisition: 'Anskaffning',
  opening_balance: 'Ingående balans',
  depreciation: 'Avskrivning',
  disposal_sale: 'Försäljning',
  disposal_scrap: 'Utrangering',
  disposal_insurance: 'Försäkringsersättning',
  partial_disposal: 'Delavyttring',
  writedown: 'Nedskrivning',
  writedown_reversal: 'Återförd nedskrivning',
  revaluation: 'Uppskrivning',
  adjustment: 'Justering',
  correction: 'Rättelse',
  reclassification: 'Omklassificering',
};

/**
 * Client-side draft schedule for the CreateAssetForm review step.
 * Mirrors the backend engine's convention: linear monthly amount
 * round((depreciable) / months, 2) with last-month remainder absorption so
 * the schedule sums exactly to the depreciable amount (no öre drift).
 * Nothing here posts anything — it is a preview only; the backend engine is
 * the source of truth once the asset is created.
 */
export const buildDraftSchedule = ({
  amount,
  residualValue = 0,
  usefulMonths,
  startDate,
  openingAccDepreciation = 0,
  openingPeriod = null,
}) => {
  const depreciable = round2((parseFloat(amount) || 0) - (parseFloat(residualValue) || 0));
  const months = parseInt(usefulMonths, 10) || 1;
  const serviceMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

  let firstPeriod = serviceMonth;
  let remainingAmount = depreciable;
  let remainingMonths = months;

  const openingAcc = round2(openingAccDepreciation);
  if (openingAcc > 0 && openingPeriod) {
    const takeoverDate = periodToDate(openingPeriod);
    if (takeoverDate) {
      const elapsed = Math.max(monthDiff(serviceMonth, takeoverDate), 0);
      firstPeriod = takeoverDate;
      remainingAmount = round2(depreciable - openingAcc);
      remainingMonths = Math.max(months - elapsed, 1);
    }
  }

  if (remainingAmount <= 0 || remainingMonths <= 0) {
    return { rows: [], monthlyAmount: 0, endDate: firstPeriod, firstPostingDate: null };
  }

  const monthly = round2(remainingAmount / remainingMonths);
  const rows = [];
  for (let i = 0; i < remainingMonths; i += 1) {
    const periodDate = addMonths(firstPeriod, i);
    const isLast = i === remainingMonths - 1;
    rows.push({
      period: dateToPeriod(periodDate),
      periodDate,
      postingDate: lastDayOfMonth(periodDate),
      amount: isLast ? round2(remainingAmount - monthly * (remainingMonths - 1)) : monthly,
    });
  }

  return {
    rows,
    monthlyAmount: monthly,
    endDate: rows[rows.length - 1].periodDate,
    firstPostingDate: rows[0].postingDate,
  };
};
